/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import styles from './editSustainability.module.scss';
import { Certificado, ObraProperties } from '../../../../../types/ObraDetails';
import { ValidationError } from '../../../../../types/ValidationError';
import { SelectOption } from '../../../../../types/Option';
import { useTranslation } from 'react-i18next';
import TitleHelp from '../../../components/titleHelp/titleHelp';
import { SaveContext } from '../../../../../context/SaveProvider';
import ObrasAPI from '../../../../../services/APIObras';
import NoDataPicture from '../../../components/noDataPicture/noDataPicture';
import sustainabilityNoData from '../../../../../assets/sustainabilityNoData.svg';
import EditSustainabilityRow from './editSuistainabilityRow/editSustainabilityRow';

interface EditSustainabilityProps {
  obra: ObraProperties;
  errors?: ValidationError[];
}

interface CertificateMasterOption extends SelectOption {
  niveles: SelectOption[];
}

// We use this method to avoid the useEffect execution in the first render
const useDidMountEffect = (func: () => void, deps: React.DependencyList) => {
  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
  }, deps);
};

const editSustainability: FC<EditSustainabilityProps> = (props) => {
  const { obra, errors } = props;
  const [t] = useTranslation();
  const [certificates, setCertificates] = useState<CertificateMasterOption[]>([]);
  const [projectCertificates, setProjectCertificates] = useState(obra.esg.certificadosSostenibilidad || []);
  const saveContext = useContext(SaveContext);
  const api = ObrasAPI.getInstance();

  useDidMountEffect(() => {
    obra.esg.certificadosSostenibilidad = projectCertificates;
    saveContext.handleChangeSave(true);
  }, [projectCertificates]);

  useEffect(() => {
    let active = true;

    const loadCertificates = async () => {
      const [certificatesResults, certificateLevelsResults] = await Promise.all([api.getCertificates(), api.getCertificatesLevel()]);

      const parsedCertificates = (certificatesResults.data?.results || [])
        .filter((c: { nombre: string }) => !['breeam new construction', 'otros'].includes(c.nombre.toLowerCase()))
        .map((c: { codigo: string; nombre: string }) => {
          const currentCertificateLevels = (certificateLevelsResults.data?.results || [])
            .filter((level: { codigo: string }) => level.codigo === c.codigo)
            .map((level: { codigo: string; nombre: string }) => ({
              id: parseInt(level.codigo, 10),
              nombre: level.nombre,
            }));

          return {
            id: parseInt(c.codigo, 10),
            nombre: c.nombre,
            niveles: currentCertificateLevels,
          };
        });

      if (active) setCertificates(parsedCertificates);
    };

    loadCertificates();

    return () => {
      active = false;
    };
  }, [api]);

  const handleAddDocument = () => {
    const newCertificate: Certificado = { nomCertificado: '', nomNivel: '' };
    setProjectCertificates([...projectCertificates, newCertificate]);
    if (obra.esg.certificadosSostenibilidad) {
      obra.esg.certificadosSostenibilidad.push(newCertificate);
    } else {
      obra.esg.certificadosSostenibilidad = [newCertificate];
    }
    saveContext.handleChangeSave(true);
  };

  const getLevelCECOC = (codCertificate: string) => {
    const certificate = certificates.find((item) => item.id === parseInt(codCertificate, 10));
    return certificate?.niveles || [];
  };

  const getCertificateOptionsForRow = (currentCert: Certificado) => {
    const selectedCodes = new Set(
      projectCertificates
        .filter((cert) => cert !== currentCert && cert.codCertificado)
        .map((cert) => String(cert.codCertificado)),
    );

    return certificates.filter((certificate) =>
      String(currentCert.codCertificado) === String(certificate.id)
      || !selectedCodes.has(String(certificate.id)));
  };

  const isFieldError = (field: string) => {
    return !!(errors && errors.find((e) => e.field === field || e.fieldField === field));
  };

  return (
    <div data-testid='edit-sustainability-test' className={styles.containerSustainability}>
      <div data-testid="sustainability-edit-test" >
        <TitleHelp
          id='sustainability'
          value={t('details.esg.sustainability.helper')}
          help={t('help.esg.sustainabilityCertificates')}
          addItemText={t('details.esg.sustainability.add') as string}
          handleAddItem={handleAddDocument}
          error={isFieldError('documentacionProyecto') || isFieldError('codNivel') || isFieldError('codNivelActualizado') ? ' ' : null}
        />
        {
          projectCertificates?.map((cert, index) => {
            const handleChangeCertificate = (data: { id: number; nombre: string }) => {
              cert.niveles = certificates.find((c) => c.id === data.id)?.niveles;
              cert.nomCertificado = data.nombre;
              cert.codCertificado = String(data.id);
              cert.nomNivel = '';
              cert.codNivel = '';
              setProjectCertificates([...projectCertificates]);
            };

            const handleChangeLevel = (data: { id: number; nombre: string }) => {
              if (cert.origen === 'cecoc') {
                cert.nomNivelActualizado = data.nombre;
                cert.codNivelActualizado = String(data.id);
              } else {
                cert.nomNivel = data.nombre;
                cert.codNivel = String(data.id);
              }
              setProjectCertificates([...projectCertificates]);
            };

            const handleDelete = () => {
              projectCertificates.splice(index, 1);
              setProjectCertificates([...projectCertificates]);
            };

            if (!cert.niveles && certificates && certificates.length > 0) {
              const certMaster = cert.codCertificado ? certificates.filter((c) => c.id === parseInt(cert.codCertificado!, 10)) : certificates;
              cert.niveles = certMaster && certMaster.length > 0 ? certMaster[0].niveles : [];
            }
            const isFieldError = (field: string) => {
              return !!(errors && errors.find((e) => (e.field === field || e.fieldField === field) && e.index === index.toString()));
            };

            return <EditSustainabilityRow
              key={index}
              index={index}
              cert={cert}
              certificateOptions={getCertificateOptionsForRow(cert)}
              getLevelCECOC={getLevelCECOC}
              handleChangeCertificate={handleChangeCertificate}
              handleChangeLevel={handleChangeLevel}
              handleDelete={cert.origen === 'cecoc' ? undefined : handleDelete}
              isFieldError={isFieldError}
            />;
          })
        }
        {
          !obra.esg.certificadosSostenibilidad || obra.esg.certificadosSostenibilidad.length === 0 ?
            <div>
              < NoDataPicture
                id='designer-nodata'
                picture={sustainabilityNoData}
                text={t('details.esg.sustainability.noDataAdd')}
              />
            </div>
            : null
        }
      </div >
    </div>);
};

export default editSustainability;
